import React, { useState, useEffect } from 'react';
import { appSearchService, appUserReviewService } from '../../lib/services/appstore/index.js';
import { useTranslation } from '../../lib/i18n';
import LanguageSwitcher from '../Common/LanguageSwitcher';
import './EnhancedAppStore.css';

const EnhancedAppStore = ({ currentTenant, currentUser }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('discover');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [trendingApps, setTrendingApps] = useState([]);
  const [recentApps, setRecentApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Advanced search filters
  const [filters, setFilters] = useState({
    category: '',
    subcategory: '',
    gradeLevel: '',
    priceRange: { min: 0, max: 100 },
    rating: 0,
    sortBy: 'rating',
    sortOrder: 'desc',
    isFree: null,
    hasTrial: null,
    tags: []
  });

  // Search suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [currentTenant]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch trending apps with error handling
      try {
        const trendingResponse = await appSearchService.getTrendingApps(currentTenant, 8);
        setTrendingApps(trendingResponse.data || []);
      } catch (err) {
        console.error('Error fetching trending apps:', err);
        setTrendingApps([]);
      }

      // Fetch recently added apps with error handling
      try {
        const recentResponse = await appSearchService.getRecentlyAddedApps(currentTenant, 8);
        setRecentApps(recentResponse.data || []);
      } catch (err) {
        console.error('Error fetching recent apps:', err);
        setRecentApps([]);
      }

      // Fetch recommendations with error handling
      try {
        if (currentUser) {
          const recommendationsResponse = await appSearchService.getAppRecommendations(currentUser.id, currentTenant, 6);
          setRecommendations(recommendationsResponse.data || []);
        } else {
          setRecommendations([]);
        }
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        setRecommendations([]);
      }

    } catch (err) {
      console.error('Error in fetchInitialData:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await appSearchService.advancedSearch({
        query: searchQuery,
        ...filters
      }, currentTenant);
      setSearchResults(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSuggestion = async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await appSearchService.getSearchSuggestions(query, currentTenant, 5);
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Failed to get suggestions:', err);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      subcategory: '',
      gradeLevel: '',
      priceRange: { min: 0, max: 100 },
      rating: 0,
      sortBy: 'rating',
      sortOrder: 'desc',
      isFree: null,
      hasTrial: null,
      tags: []
    });
  };

  const handleAppInstall = async (appId) => {
    // This would integrate with the installation service
    console.log('Installing app:', appId);
    // TODO: Implement actual installation logic
  };

  const handleAppDetails = (appId) => {
    // Navigate to app details page
    console.log('Viewing app details:', appId);
    // TODO: Implement navigation to app details
  };

  const renderAppCard = (app) => (
    <div key={app.id} className="app-card">
      <div className="app-icon">
        {app.app_icon_url ? (
          <img src={app.app_icon_url} alt={app.app_name} />
        ) : (
          <div className="app-icon-placeholder">
            {app.app_name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="app-info">
        <h3>{app.app_name}</h3>
        <p className="app-description">{app.app_description}</p>
        <div className="app-meta">
          <span className="app-category">{app.app_category}</span>
          <span className="app-rating">⭐ {app.rating || 'N/A'}</span>
          {app.is_free && <span className="app-free">{t('enhancedAppStore.free')}</span>}
          {app.has_trial && <span className="app-trial">{t('enhancedAppStore.trial')}</span>}
        </div>
        <div className="app-stats">
          <span>{app.total_downloads || 0} {t('enhancedAppStore.downloads')}</span>
          <span>{app.total_reviews || 0} {t('enhancedAppStore.reviews')}</span>
        </div>
        <div className="app-developer">
          <span>{t('enhancedAppStore.by')} {app.app_developer?.developer_name || t('enhancedAppStore.unknown')}</span>
        </div>
      </div>
      <div className="app-actions">
        <button 
          className="install-btn"
          onClick={() => handleAppInstall(app.id)}
        >
          {t('enhancedAppStore.install')}
        </button>
        <button 
          className="details-btn"
          onClick={() => handleAppDetails(app.id)}
        >
          {t('enhancedAppStore.details')}
        </button>
      </div>
    </div>
  );

  const renderAdvancedFilters = () => (
    <div className="advanced-filters">
      <h3>{t('enhancedAppStore.advancedFilters')}</h3>
      <div className="filters-grid">
        <div className="filter-group">
          <label>{t('enhancedAppStore.category')}</label>
          <select 
            value={filters.category} 
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <option value="">{t('enhancedAppStore.allCategories')}</option>
            <option value="education">{t('categories.education')}</option>
            <option value="productivity">{t('categories.productivity')}</option>
            <option value="entertainment">{t('categories.entertainment')}</option>
            <option value="tools">{t('categories.utilities')}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('enhancedAppStore.gradeLevel')}</label>
          <select 
            value={filters.gradeLevel} 
            onChange={(e) => handleFilterChange('gradeLevel', e.target.value)}
          >
            <option value="">{t('enhancedAppStore.allGrades')}</option>
            <option value="elementary">{t('enhancedAppStore.elementary')}</option>
            <option value="middle">{t('enhancedAppStore.middleSchool')}</option>
            <option value="high">{t('enhancedAppStore.highSchool')}</option>
            <option value="college">{t('enhancedAppStore.college')}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('enhancedAppStore.rating')}</label>
          <select 
            value={filters.rating} 
            onChange={(e) => handleFilterChange('rating', parseInt(e.target.value))}
          >
            <option value={0}>{t('enhancedAppStore.anyRating')}</option>
            <option value={4}>4+ {t('enhancedAppStore.stars')}</option>
            <option value={3}>3+ {t('enhancedAppStore.stars')}</option>
            <option value={2}>2+ {t('enhancedAppStore.stars')}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('enhancedAppStore.price')}</label>
          <select 
            value={filters.isFree === null ? '' : filters.isFree} 
            onChange={(e) => handleFilterChange('isFree', e.target.value === '' ? null : e.target.value === 'true')}
          >
            <option value="">{t('enhancedAppStore.allPrices')}</option>
            <option value="true">{t('enhancedAppStore.free')}</option>
            <option value="false">{t('enhancedAppStore.paid')}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('enhancedAppStore.sortBy')}</label>
          <select 
            value={filters.sortBy} 
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
          >
            <option value="rating">{t('enhancedAppStore.sortOptions.rating')}</option>
            <option value="downloads">{t('enhancedAppStore.sortOptions.downloads')}</option>
            <option value="reviews">{t('enhancedAppStore.sortOptions.reviews')}</option>
            <option value="name">{t('enhancedAppStore.sortOptions.name')}</option>
            <option value="date">{t('enhancedAppStore.sortOptions.dateAdded')}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('enhancedAppStore.order')}</label>
          <select 
            value={filters.sortOrder} 
            onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
          >
            <option value="desc">{t('enhancedAppStore.descending')}</option>
            <option value="asc">{t('enhancedAppStore.ascending')}</option>
          </select>
        </div>
      </div>
      
      <div className="filter-actions">
        <button onClick={handleSearch} className="apply-filters-btn">
          {t('enhancedAppStore.applyFilters')}
        </button>
        <button onClick={clearFilters} className="clear-filters-btn">
          {t('enhancedAppStore.clearAll')}
        </button>
      </div>
    </div>
  );

  if (loading && !searchResults.length) {
    return (
      <div className="enhanced-app-store">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}...</p>
        </div>
      </div>
    );
  }

        if (error) {
        return (
          <div className="enhanced-app-store">
            <div className="error-container">
              <h3>{t('common.error')}</h3>
              <p>{error}</p>
              <button 
                onClick={() => {
                  setError(null);
                  fetchInitialData();
                }}
                className="retry-button"
              >
                🔄 {t('enhancedAppStore.retryButton')}
              </button>
            </div>
          </div>
        );
      }

  return (
    <div className="enhanced-app-store">
      {/* Header */}
      <div className="app-store-header">
        <h1>{t('enhancedAppStore.title')}</h1>
        <p>{t('enhancedAppStore.description')}</p>
        <LanguageSwitcher />
      </div>

      {/* Navigation Tabs */}
      <div className="app-store-tabs">
        <button 
          className={`tab ${activeTab === 'discover' ? 'active' : ''}`}
          onClick={() => setActiveTab('discover')}
        >
          {t('enhancedAppStore.discover')}
        </button>
        <button 
          className={`tab ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          {t('enhancedAppStore.search')}
        </button>
        <button 
          className={`tab ${activeTab === 'recommendations' ? 'active' : ''}`}
          onClick={() => setActiveTab('recommendations')}
        >
          {t('enhancedAppStore.recommendations')}
        </button>
      </div>

      {/* Search Section */}
      {activeTab === 'search' && (
        <div className="search-section">
          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder={t('enhancedAppStore.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearchSuggestion(e.target.value);
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                onFocus={() => setShowSuggestions(true)}
              />
              <button onClick={handleSearch}>{t('common.search')}</button>
              
              {/* Search Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="search-suggestions">
                  {suggestions.map((suggestion, index) => (
                    <div 
                      key={index} 
                      className="suggestion-item"
                      onClick={() => {
                        setSearchQuery(suggestion.app_name);
                        setShowSuggestions(false);
                        handleSearch();
                      }}
                    >
                      {suggestion.app_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {renderAdvancedFilters()}
        </div>
      )}

      {/* Content Sections */}
      <div className="app-store-content">
        {/* Search Results */}
        {activeTab === 'search' && searchResults.length > 0 && (
          <div className="search-results-section">
            <h2>
              {t('enhancedAppStore.searchResults')} ({searchResults.length})
              <button 
                className="clear-btn"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  clearFilters();
                }}
              >
                {t('common.clear')}
              </button>
            </h2>
            <div className="apps-grid">
              {searchResults.map(renderAppCard)}
            </div>
          </div>
        )}

        {/* Discover Tab */}
        {activeTab === 'discover' && (
          <>
            {/* Trending Apps */}
            <div className="trending-section">
              <h2>{t('enhancedAppStore.trending')}</h2>
              <div className="apps-grid">
                {trendingApps.map(renderAppCard)}
              </div>
            </div>

            {/* Recently Added */}
            <div className="recent-section">
              <h2>{t('enhancedAppStore.recentlyAdded')}</h2>
              <div className="apps-grid">
                {recentApps.map(renderAppCard)}
              </div>
            </div>
          </>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div className="recommendations-section">
            <h2>{t('enhancedAppStore.recommended')}</h2>
            {recommendations.length > 0 ? (
              <div className="apps-grid">
                {recommendations.map(renderAppCard)}
              </div>
            ) : (
              <div className="no-recommendations">
                <p>{t('enhancedAppStore.noRecommendations')}</p>
              </div>
            )}
          </div>
        )}

        {/* No Results */}
        {activeTab === 'search' && searchQuery && searchResults.length === 0 && !loading && (
          <div className="no-results">
            <p>{t('enhancedAppStore.noResults')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedAppStore;
